import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotification } from '../context/NotificationContext';
import axios from 'axios';
import { API_URL } from '../config/api';
import { getFullImageUrl as getFullImageUrlUtil } from '../utils/imageUtils';
import './MediaSelector.css';

const MediaSelector = ({ onSelect, currentValue, category, multiple = false, restaurantId, storeId }) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useNotification();
  const [showGallery, setShowGallery] = useState(false);
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedImages, setSelectedImages] = useState(
    multiple ? (Array.isArray(currentValue) ? currentValue : []) : []
  );

  useEffect(() => {
    if (showGallery) {
      fetchMedia();
      // Sync selectedImages with currentValue when gallery opens
      if (multiple) {
        const normalized = Array.isArray(currentValue) 
          ? currentValue.map(normalizeUrl)
          : [];
        setSelectedImages(normalized);
      }
    }
  }, [showGallery, category, currentValue, multiple, restaurantId, storeId]);

  const fetchMedia = async () => {
    setLoading(true);
    try {
      // Build query parameters
      const params = new URLSearchParams();
      if (category) {
        params.append('category', category);
      }
      if (restaurantId) {
        params.append('restaurant', restaurantId);
      }
      if (storeId) {
        params.append('store', storeId);
      }
      
      const url = `${API_URL}/media${params.toString() ? '?' + params.toString() : ''}`;
      const response = await axios.get(url);
      
      // If category is specified, also fetch general category and combine
      if (category) {
        const generalParams = new URLSearchParams();
        generalParams.append('category', 'general');
        if (restaurantId) {
          generalParams.append('restaurant', restaurantId);
        }
        if (storeId) {
          generalParams.append('store', storeId);
        }
        const generalUrl = `${API_URL}/media?${generalParams.toString()}`;
        const generalResponse = await axios.get(generalUrl);
        
        // Combine and deduplicate
        const combined = [...(response.data || []), ...(generalResponse.data || [])];
        const unique = combined.filter((item, index, self) => 
          index === self.findIndex(t => t._id === item._id)
        );
        setMedia(unique);
      } else {
        setMedia(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching media:', error);
      console.error('Error details:', error.response?.data || error.message);
      setMedia([]);
      // Show error to user if possible
      if (error.response?.status === 401) {
        console.error('Authentication required to view media gallery');
      }
    } finally {
      setLoading(false);
    }
  };

  // Use the shared utility function for consistency
  const getFullImageUrl = (url) => {
    return getFullImageUrlUtil(url);
  };

  const normalizeUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // Normalize to relative path format (without /api prefix)
    if (url.startsWith('/api/')) {
      return url.replace('/api', '');
    }
    return url;
  };

  const handleImageSelect = (relativePath) => {
    // relativePath is already normalized (from item.url)
    if (multiple) {
      const normalizedCurrent = Array.isArray(currentValue) 
        ? currentValue.map(normalizeUrl)
        : [];
      const newSelection = normalizedCurrent.includes(relativePath)
        ? normalizedCurrent.filter(url => url !== relativePath)
        : [...normalizedCurrent, relativePath];
      setSelectedImages(newSelection);
      onSelect(newSelection);
    } else {
      onSelect(relativePath);
      setShowGallery(false);
    }
  };

  const handleRemove = (imageUrl) => {
    const normalizedUrl = normalizeUrl(imageUrl);
    if (multiple) {
      const normalizedCurrent = Array.isArray(currentValue) 
        ? currentValue.map(normalizeUrl)
        : [];
      const newSelection = normalizedCurrent.filter(url => url !== normalizedUrl);
      setSelectedImages(newSelection);
      onSelect(newSelection);
    } else {
      onSelect('');
    }
  };

  const getCurrentImages = () => {
    if (multiple) {
      return Array.isArray(currentValue) ? currentValue : [];
    }
    return currentValue ? [currentValue] : [];
  };

  const currentImages = getCurrentImages();

  // Helper to check if an image is selected (comparing normalized URLs)
  const isImageSelected = (imageUrl) => {
    const normalizedUrl = normalizeUrl(imageUrl);
    if (multiple) {
      const normalizedCurrent = Array.isArray(currentValue) 
        ? currentValue.map(normalizeUrl)
        : [];
      return normalizedCurrent.includes(normalizedUrl);
    }
    return normalizeUrl(currentValue) === normalizedUrl;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      showError(t('admin.onlyImagesAllowed'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      showError(t('admin.fileTooLarge'));
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('image', file);
      formData.append('category', category || 'general');
      if (restaurantId) {
        formData.append('restaurant', restaurantId);
      }
      if (storeId) {
        formData.append('store', storeId);
      }

      const token = localStorage.getItem('token');
      const headers = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      // Don't set Content-Type for FormData - let axios set it with boundary
      const response = await axios.post(`${API_URL}/media/upload`, formData, {
        headers
      });

      // Refresh media list if gallery is open
      if (showGallery) {
        await fetchMedia();
      }
      
      // Select the newly uploaded image
      const normalizedPath = normalizeUrl(response.data.url);
      if (multiple) {
        const normalizedCurrent = Array.isArray(currentValue) 
          ? currentValue.map(normalizeUrl)
          : [];
        const newSelection = [...normalizedCurrent, normalizedPath];
        onSelect(newSelection);
      } else {
        onSelect(normalizedPath);
      }
      
      e.target.value = ''; // Reset file input
      showSuccess(t('admin.mediaUploaded'));
    } catch (error) {
      console.error('Error uploading media:', error);
      showError(t('admin.errorUploadingMedia') + ': ' + (error.response?.data?.message || error.message));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="media-selector">
      <div className="selected-images-preview">
        {currentImages.map((url, index) => {
          const displayUrl = getFullImageUrl(url);
          return (
            <div key={index} className="selected-image-item">
              <img src={displayUrl} alt={`Selected ${index + 1}`} />
              <button
                type="button"
                className="remove-image-btn"
                onClick={() => handleRemove(url)}
                title={t('admin.remove')}
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      
      <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="select-from-gallery-btn"
          onClick={() => setShowGallery(true)}
        >
          {multiple ? t('admin.selectFromGallery') : (currentValue ? t('admin.changeImage') : t('admin.selectFromGallery'))}
        </button>
        {(restaurantId || storeId) && (
          <label className="upload-image-btn" style={{ display: 'inline-block' }}>
            {uploading ? t('admin.uploading') : t('admin.uploadImage')}
            <input
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              disabled={uploading}
              style={{ display: 'none' }}
            />
          </label>
        )}
      </div>

      {showGallery && (
        <div className="gallery-modal-overlay" onClick={() => setShowGallery(false)}>
          <div className="gallery-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="gallery-modal-header">
              <h3>{t('admin.selectFromGallery')}</h3>
              <button className="close-gallery-btn" onClick={() => setShowGallery(false)}>×</button>
            </div>
            
            <div className="gallery-modal-body">
              {loading ? (
                <div className="gallery-loading">{t('admin.loading')}</div>
              ) : media.length === 0 ? (
                <div className="gallery-loading" style={{ color: '#e74c3c', padding: '40px' }}>
                  {t('admin.noMediaFound')}. {t('admin.uploadImagesInMediaGallery')}
                </div>
              ) : (
                <div className="gallery-grid">
                  {media.map(item => {
                    const imageUrl = getFullImageUrl(item.url);
                    const normalizedPath = normalizeUrl(item.url);
                    const isSelected = isImageSelected(item.url);
                    
                    return (
                      <div
                        key={item._id}
                        className={`gallery-item ${isSelected ? 'selected' : ''}`}
                        onClick={() => handleImageSelect(normalizedPath)}
                      >
                        <img src={imageUrl} alt={item.originalName} />
                        {isSelected && <div className="selected-checkmark">✓</div>}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {multiple && (
              <div className="gallery-modal-footer">
                <button
                  className="confirm-selection-btn"
                  onClick={() => {
                    onSelect(selectedImages);
                    setShowGallery(false);
                  }}
                >
                  {t('admin.confirmSelection')} ({selectedImages.length})
                </button>
                <button
                  className="cancel-selection-btn"
                  onClick={() => setShowGallery(false)}
                >
                  {t('admin.cancel')}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaSelector;
